import { CamposView } from './views/CamposView';
import { SitesView } from './views/SitesView';
import { EquipeView } from './views/EquipeView';
import { acharContato, complementoContato, novoContato } from './lib/contatos';
import { CONTA_COMO_PERDA, aoTrocarEtapa, paraRepescar, proximaAbordagem } from './lib/pipeline';
import { guardado } from './lib/armazenamento';
import React, { useEffect, useRef, useState } from 'react';
import { AuditSidebar } from './components/AuditSidebar';
import { GlobalSearchModal } from './components/GlobalSearchModal';
import { NotificationBell } from './components/NotificationBell';
import { Confetti, Modal, NavIcon, Spinner, TemaToggle, Toast } from './components/base';
import { LeadDetailModal } from './features/leads/LeadDetailModal';
import { LeadForm } from './features/leads/LeadForm';
import { STAGES } from './lib/constantes';
import { paleta, useEmpresas } from './lib/empresas';
import { FieldValue, auth, db } from './lib/firebase';
import { addDaysISO, genToken } from './lib/formato';
import { playSwipeSound, playWinSound } from './lib/som';
import { CalendarView } from './views/CalendarView';
import { ContatosView } from './views/ContatosView';
import { ConcluidasView } from './views/ConcluidasView';
import { DashboardView } from './views/DashboardView';
import { KPIsView } from './views/KPIsView';
import { MesView } from './views/MesView';
import { KanbanView } from './views/KanbanView';
import { LostLeadsView } from './views/LostLeadsView';
import { PartnersView } from './views/PartnersView';
import { PendingReferralsView } from './views/PendingReferralsView';
import { RepAssesView } from './views/RepAssesView';
import { SellersView } from './views/SellersView';
import { TodosView } from './views/TodosView';

function MainApp({ user, userDoc }) {
  const [leads, setLeads]       = useState([]);
  const [partners, setPartners] = useState([]);
  const [sellers, setSellers]   = useState([]);
  const [contatos, setContatos] = useState([]);
  const [camposCustom, setCamposCustom] = useState([]); // definição por empresa (roadmap #24)
  // Vendedor padrão da empresa. Mesma config que a Cloud Function receberLead lê,
  // para o lead criado à mão nascer com o mesmo responsável do que entra pelo site.
  const [vendedorPadrao, setVendedorPadrao] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [tab, setTab]         = useState('kanban');
  // Sidebar visível por padrão: a HIG pede para não esconder, senão ninguém descobre
  const [sidebarOpen, setSidebarOpen] = useState(() => guardado.ler('sidebarOpen') !== '0');
  useEffect(() => { guardado.gravar('sidebarOpen', sidebarOpen ? '1' : '0'); }, [sidebarOpen]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [editingLead, setEditingLead]   = useState(null);
  const [showNewLead, setShowNewLead]   = useState(false);
  const [migrating, setMigrating]       = useState(false);
  const [showAudit, setShowAudit]       = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [toast, setToast]               = useState(null);
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const showToast = (message, type='success') => setToast({ message, type, key: Date.now() });

  // ── Audit helpers ──
  const auditCol = () => db.collection('companies').doc(company).collection('audit_log');
  const writeAudit = async (entry) => {
    try {
      await auditCol().add({ ...entry, timestamp: new Date().toISOString(), user_email: user.email, user_name: user.displayName || user.email });
    } catch(e) { /* non-fatal */ }
  };

  // ── Rollback de auditoria ──
  const handleRollback = async (entry) => {
    const now = new Date().toISOString();
    try {
      switch (entry.type) {
        case 'stage_change':
          await leadsCol().doc(entry.lead_id).update({ stage: entry.before.stage, updated_at: now });
          break;
        case 'lead_create':
          await leadsCol().doc(entry.lead_id).delete();
          break;
        case 'lead_update':
          if (entry.before) {
            const { id: _id, ...rest } = entry.before;
            await leadsCol().doc(entry.lead_id).update({ ...rest, updated_at: now });
          }
          break;
        case 'lead_delete':
          if (entry.before) {
            const { id: _id2, ...rest2 } = entry.before;
            await leadsCol().doc(entry.lead_id).set({ ...rest2, updated_at: now });
          }
          break;
        case 'interaction_add':
          await patchArray(entry.lead_id, 'interactions', arr => arr.filter(i=>i.id!==entry.after?.interaction_id));
          break;
        case 'interaction_delete':
          if (entry.before?.interactions) await leadsCol().doc(entry.lead_id).update({ interactions: entry.before.interactions, updated_at: now });
          break;
        case 'timeline_add':
          await patchArray(entry.lead_id, 'timeline', arr => arr.filter(e2=>e2.id!==entry.after?.entry_id));
          break;
        case 'timeline_delete':
          if (entry.before?.timeline) await leadsCol().doc(entry.lead_id).update({ timeline: entry.before.timeline, updated_at: now });
          break;
        case 'deal_update':
          if (entry.before) await leadsCol().doc(entry.lead_id).update({ deal: entry.before.deal, updated_at: now });
          break;
        default: break;
      }
      await writeAudit({ type:'rollback', lead_id: entry.lead_id, lead_name: entry.lead_name, description:`↩️ Reverteu: "${entry.description}"`, before:null, after:null });
    } catch(e) { alert('Erro ao reverter: ' + (e.message||e)); }
  };

  // Se admin_companies existe, é admin apenas nessas empresas; caso contrário usa o role global
  const isAdmin = userDoc?.admin_companies
    ? userDoc.admin_companies.includes(company)
    : userDoc?.role === 'admin';
  // Editor pode criar/atualizar leads e interações, mas não excluir nem gerenciar parceiros/vendedores
  const canEdit = isAdmin || userDoc?.role === 'editor';
  // Empresas do usuário, lidas do banco (fase A). Cadastro de cliente novo não
  // depende mais de deploy: basta o documento em `companies/{id}`.
  const { empresas: allowedCompanies, porId: empresasPorId } = useEmpresas(userDoc?.companies);

  // Empresa selecionada — persiste entre sessões, respeitando o acesso
  const [company, setCompany] = useState(() => {
    const saved = guardado.ler('selectedCompany');
    const accessible = userDoc?.companies || ['produxia'];
    return (saved && accessible.includes(saved)) ? saved : (accessible[0] || 'produxia');
  });

  // Helpers de coleção dinâmica
  const leadsCol    = () => db.collection('companies').doc(company).collection('leads');
  const partnersCol = () => db.collection('companies').doc(company).collection('partners');
  const sellersCol  = () => db.collection('companies').doc(company).collection('sellers');

  // Trocar empresa
  const switchCompany = (id) => {
    guardado.gravar('selectedCompany', id);
    setCompany(id);
    setTab('kanban');
    setSelectedLead(null);
    setEditingLead(null);
    setShowNewLead(false);
  };

  // ── Migração automática: raiz /leads → companies/produxia/leads ──
  useEffect(() => {
    if (company !== 'produxia') return;
    const migrate = async () => {
      try {
        const [subSnap, rootSnap] = await Promise.all([
          db.collection('companies').doc('produxia').collection('leads').limit(1).get(),
          db.collection('leads').get()
        ]);
        if (!subSnap.empty || rootSnap.empty) return; // já migrado ou nada para migrar
        setMigrating(true);
        const batch = db.batch();
        rootSnap.docs.forEach(doc => {
          const ref = db.collection('companies').doc('produxia').collection('leads').doc(doc.id);
          batch.set(ref, doc.data());
        });
        // Migrar parceiros também
        const rootPartners = await db.collection('partners').get();
        rootPartners.docs.forEach(doc => {
          const ref = db.collection('companies').doc('produxia').collection('partners').doc(doc.id);
          batch.set(ref, doc.data());
        });
        await batch.commit();
        setMigrating(false);
      } catch(e) { setMigrating(false); }
    };
    migrate();
  }, [company]);

  // ── Listeners em tempo real (re-dispara ao mudar empresa) ──
  useEffect(() => {
    setLoading(true);
    setLeads([]);
    setPartners([]);
    setSellers([]);
    setReferrals([]);
    let ready = { leads:false, partners:false, sellers:false };
    const check = () => { if(ready.leads && ready.partners && ready.sellers) setLoading(false); };

    const unsubLeads = db.collection('companies').doc(company).collection('leads')
      .orderBy('created_at','desc')
      .onSnapshot(snap => { setLeads(snap.docs.map(d=>({id:d.id,...d.data()}))); ready.leads=true; check(); });

    const unsubPartners = db.collection('companies').doc(company).collection('partners')
      .orderBy('name')
      .onSnapshot(snap => { setPartners(snap.docs.map(d=>({id:d.id,...d.data()}))); ready.partners=true; check(); });

    const unsubSellers = db.collection('companies').doc(company).collection('sellers')
      .orderBy('name')
      .onSnapshot(snap => { setSellers(snap.docs.map(d=>({id:d.id,...d.data()}))); ready.sellers=true; check(); });

    const unsubReferrals = db.collection('companies').doc(company).collection('referrals')
      .orderBy('created_at','desc')
      .onSnapshot(snap => { setReferrals(snap.docs.map(d=>({id:d.id,...d.data()}))); }, ()=>{});

    // Contatos. Se a regra do Firestore ainda não permitir, falha em silêncio:
    // a tela mostra vazio em vez de derrubar o app.
    const unsubContatos = db.collection('companies').doc(company).collection('contatos')
      .onSnapshot(snap => { setContatos(snap.docs.map(d=>({id:d.id,...d.data()}))); },
                  (e) => { console.warn('Contatos indisponíveis:', e.message); setContatos([]); });

    const unsubAtrib = db.collection('companies').doc(company).collection('config').doc('atribuicao')
      .onSnapshot(doc => {
        const d = doc.exists ? doc.data() : null;
        setVendedorPadrao(d && d.seller_id ? { id: d.seller_id, nome: d.seller_name || '' } : null);
      }, (e) => { console.warn('Sem vendedor padrão:', e.message); setVendedorPadrao(null); });

    // Campos customizados da empresa. Falha em silêncio: sem config, o app segue igual.
    const unsubCampos = db.collection('companies').doc(company).collection('config').doc('campos')
      .onSnapshot(doc => { setCamposCustom(doc.exists ? (doc.data().campos || []) : []); },
                  (e) => { console.warn('Campos customizados indisponíveis:', e.message); setCamposCustom([]); });

    return () => { unsubLeads(); unsubPartners(); unsubSellers(); unsubReferrals(); unsubContatos(); unsubCampos(); unsubAtrib(); };
  }, [company]);

  // Edita um array do lead lendo a versão fresca dentro da transação:
  // duas pessoas salvando ao mesmo tempo não se apagam.
  const patchArray = (leadId, field, fn) => db.runTransaction(async tx => {
    const ref = leadsCol().doc(leadId);
    const snap = await tx.get(ref);
    if (!snap.exists) return;
    tx.update(ref, { [field]: fn(snap.data()[field] || []), updated_at: new Date().toISOString() });
  });

  // Toda negociação pertence a um contato. Acha pelo e-mail/telefone (ou nome, se
  // não houver nenhum dos dois) na lista já carregada; senão cria. Falha aqui não
  // pode derrubar a criação do lead, por isso quem chama embrulha em try/catch.
  const vincularContato = async (leadId, dados, { lista = contatos, criados } = {}) => {
    const col = db.collection('companies').doc(company).collection('contatos');
    const now = new Date().toISOString();
    const achado = acharContato(lista, dados);
    let contactId = achado?.id;
    if (contactId) {
      await col.doc(contactId).update({ ...complementoContato(achado, dados), negociacoes: FieldValue.arrayUnion(leadId), updated_at: now });
    } else {
      const novo = { ...novoContato(dados, now), negociacoes: [leadId] };
      const ref = await col.add(novo);
      contactId = ref.id;
      if (criados) criados.push({ id: contactId, ...novo }); // num lote, a linha seguinte já enxerga este contato
    }
    await leadsCol().doc(leadId).update({ contact_id: contactId });
    return contactId;
  };

  // Toda troca de etapa vira marco na linha do tempo (roadmap #14): audit trail sem esforço.
  const stageLabel = (k) => STAGES.find(x => x.key === k)?.label || k;
  const marcoEtapa = (de, para, now) => ({
    id: `${Date.now()}-etapa`, date: now.slice(0, 10), created_at: now, auto: true,
    text: `📦 Etapa: ${stageLabel(de)} → ${stageLabel(para)}`,
  });

  // ── Repescagem ────────────────────────────────────────────
  // Lead perdido com data marcada volta para a fila de trabalho quando a data chega.
  // Não existe agendador no projeto, então a promoção acontece quando alguém abre a
  // plataforma: se ninguém abre, ninguém veria a notificação de qualquer jeito.
  // A transação relê o documento e só promove se ele AINDA estiver em 'perdido' com data
  // vencida, então dois navegadores abertos juntos não duplicam o marco na timeline.
  // `tentados` evita ficar retentando em loop um lead cuja escrita falhou.
  // 'promovendo' evita duas passadas ao mesmo tempo (o listener redispara a cada escrita).
  // 'tentados' guarda só o que FALHOU: marcar antes de tentar bloqueava a promoção legítima
  // do lead pelo resto da sessão, mesmo quando a escrita tinha dado certo.
  const tentados = useRef(new Set());
  const promovendo = useRef(false);
  useEffect(() => {
    if (loading || promovendo.current) return;
    const vencidos = paraRepescar(leads, null).filter(l => !tentados.current.has(`${company}:${l.id}`));
    if (!vencidos.length) return;
    promovendo.current = true;
    (async () => {
      let n = 0;
      for (const lead of vencidos) {
        try {
          const promovido = await db.runTransaction(async tx => {
            const ref = leadsCol().doc(lead.id);
            const snap = await tx.get(ref);
            if (!snap.exists) return false;
            const atual = { id: lead.id, ...snap.data() };
            if (!paraRepescar([atual], null).length) return false; // alguém promoveu antes
            const agora = new Date().toISOString();
            tx.update(ref, {
              ...aoTrocarEtapa(atual, 'repescagem', agora),
              stage: 'repescagem',
              updated_at: agora,
              timeline: FieldValue.arrayUnion(marcoEtapa('perdido', 'repescagem', agora)),
            });
            return true;
          });
          if (promovido) n += 1;
        } catch (e) {
          tentados.current.add(`${company}:${lead.id}`); // só o que falhou entra na lista negra
          console.warn('Repescagem não promovida:', lead.id, e);
        }
      }
      promovendo.current = false;
      if (n) showToast(`🔄 ${n} lead${n > 1 ? 's voltaram' : ' voltou'} para repescagem`, 'warning');
    })();
  }, [leads, loading, company]);

  // Comissão paga (roadmap #6): um carimbo de data por parceiro dentro do lead
  const handleTogglePartnerPaid = (leadId, partnerId) => {
    const hoje = addDaysISO(null, 0);
    return patchArray(leadId, 'partners', arr => arr.map(p => p.id === partnerId ? { ...p, paid_at: p.paid_at ? null : hoje } : p));
  };

  // Próximos passos (roadmap #8): tarefas gravadas no lead, visíveis para a equipe
  const handleAddTask = async (leadId, { text, due }) => {
    const now = new Date().toISOString();
    await leadsCol().doc(leadId).update({
      tasks: FieldValue.arrayUnion({ id: `${Date.now()}`, text, due: due || null, done: false, owner: user.email, created_at: now }),
      updated_at: now,
    });
  };
  const handleToggleTask = (leadId, taskId) =>
    patchArray(leadId, 'tasks', arr => arr.map(t => t.id === taskId ? { ...t, done: !t.done, done_at: t.done ? null : new Date().toISOString() } : t));
  const handleDeleteTask = (leadId, taskId) => patchArray(leadId, 'tasks', arr => arr.filter(t => t.id !== taskId));

  // Importação por CSV (roadmap #4): grava um a um, ligando contatos, e avisa o progresso
  const handleImportLeads = async (novos, aoAvancar) => {
    const now = new Date().toISOString();
    const criados = []; let ok = 0;
    for (const dados of novos) {
      try {
        const ref = await leadsCol().add({ ...dados, partners: [], interactions: [], created_at: now, updated_at: now, stage_changed_at: now, _fonte: 'csv' });
        try { await vincularContato(ref.id, dados, { lista: [...contatos, ...criados], criados }); } catch (e) { console.warn('Importado sem contato', e); }
        ok++;
      } catch (e) { console.error('Falha ao importar', dados.company_name, e); }
      if (aoAvancar) aoAvancar(ok);
    }
    await writeAudit({ type: 'lead_import', lead_id: null, lead_name: `${ok} leads`, description: `Importou ${ok} lead${ok === 1 ? '' : 's'} por CSV`, before: null, after: { count: ok } });
    showToast(`${ok} lead${ok === 1 ? '' : 's'} importado${ok === 1 ? '' : 's'}`);
    return ok;
  };

  const handleSalvarCampos = async (campos) => {
    try {
      await db.collection('companies').doc(company).collection('config').doc('campos')
        .set({ campos, updated_at: new Date().toISOString() }, { merge: true });
      showToast('Campos atualizados');
    } catch (e) { showToast('Erro ao salvar os campos', 'error'); }
  };

  // ── Operações de Lead ──
  const handleCreateLead = async (form) => {
    const now = new Date().toISOString();
    try {
      const ref = await leadsCol().add({ ...form, interactions:[], created_at:now, updated_at:now, stage_changed_at:now });
      setShowNewLead(false);
      try { await vincularContato(ref.id, form); } catch (e) { console.warn('Lead criado sem contato vinculado', e); }
      await writeAudit({ type:'lead_create', lead_id:ref.id, lead_name:form.company_name, description:`Criou lead "${form.company_name}"`, before:null, after:{...form,id:ref.id} });
      showToast(`Lead "${form.company_name}" criado`);
    } catch(e) { showToast('Erro ao criar lead', 'error'); }
  };

  const handleUpdateLead = async (id, form) => {
    const lead = leads.find(l=>l.id===id);
    const now = new Date().toISOString();
    const patch = { ...form, updated_at: now };
    // Etapa trocada pelo formulário segue a mesma regra do arrastar no Kanban
    if (form.stage && lead && form.stage !== lead.stage) {
      patch.stage_changed_at = now;
      patch.timeline = FieldValue.arrayUnion(marcoEtapa(lead.stage, form.stage, now));
      Object.assign(patch, aoTrocarEtapa(lead, form.stage, now, form));
    }
    try {
      await leadsCol().doc(id).update(patch);
      // lead que entrou depois da migração de contatos ganha o vínculo na primeira edição
      if (lead && !lead.contact_id) { try { await vincularContato(id, { ...lead, ...form }); } catch (e) { console.warn('Sem vínculo de contato', e); } }
      setEditingLead(null);
      await writeAudit({ type:'lead_update', lead_id:id, lead_name:form.company_name||lead?.company_name, description:`Editou lead "${form.company_name||lead?.company_name}"`, before:lead?{...lead}:null, after:{...form} });
      showToast('Lead atualizado');
    } catch(e) { showToast('Erro ao salvar', 'error'); }
  };

  const handleDeleteLead = async (id) => {
    const lead = leads.find(l=>l.id===id);
    try {
      await leadsCol().doc(id).delete();
      setSelectedLead(null);
      await writeAudit({ type:'lead_delete', lead_id:id, lead_name:lead?.company_name, description:`Excluiu lead "${lead?.company_name}"`, before:lead?{...lead}:null, after:null });
      showToast('Lead excluído', 'warning');
    } catch(e) { showToast('Erro ao excluir', 'error'); }
  };

  // Mover lead para outra empresa (copia + apaga da atual)
  const handleMoveLead = async (lead, targetCompanyId) => {
    const { id, ...data } = lead;
    const now = new Date().toISOString();
    try {
      await db.collection('companies').doc(targetCompanyId).collection('leads').add({
        ...data, moved_from: company, moved_at: now, updated_at: now,
      });
      await leadsCol().doc(id).delete();
      const targetLabel = empresasPorId[targetCompanyId]?.label || targetCompanyId;
      await writeAudit({ type:'lead_delete', lead_id:id, lead_name:lead.company_name, description:`Moveu lead "${lead.company_name}" → ${targetLabel}`, before:{...lead}, after:null });
      showToast(`Lead movido para ${targetLabel}`);
    } catch(e) { showToast('Erro ao mover lead', 'error'); }
  };

  const handleStageChange = async (leadId, stage) => {
    const lead = leads.find(l=>l.id===leadId);
    const prevStage = lead?.stage || 'backlog';
    const isFechado = stage === 'fechado';
    // Som: swipe em qualquer movimento, fanfare ao fechar
    if (isFechado) {
      playWinSound();
      setTimeout(() => setShowConfetti(true), 80);
    } else {
      playSwipeSound();
    }
    const now = new Date().toISOString();
    if (stage === prevStage) return;
    const update = { stage, updated_at: now, timeline: FieldValue.arrayUnion(marcoEtapa(prevStage, stage, now)),
                     ...aoTrocarEtapa(lead, stage, now) };
    await leadsCol().doc(leadId).update(update);
    const stgLabel = s => STAGES.find(x=>x.key===s)?.label || s;
    await writeAudit({ type:'stage_change', lead_id:leadId, lead_name:lead?.company_name, description:`Moveu "${lead?.company_name}" de ${stgLabel(prevStage)} → ${stgLabel(stage)}`, before:{stage:prevStage}, after:{stage} });
  };

  const handleToggleLeadFlag = async (leadId, field, value) => {
    await leadsCol().doc(leadId).update({ [field]: value, updated_at: new Date().toISOString() });
  };

  // repeat=true grava o ritmo no lead (followup_days); false grava só a data e desliga o ritmo, se havia.
  const handleSnoozeDate = async (leadId, days, repeat = false) => {
    const dateStr = addDaysISO(null, days);
    await leadsCol().doc(leadId).update({
      next_contact_date: dateStr,
      followup_days: repeat ? Number(days) : null,
      updated_at: new Date().toISOString(),
    });
    showToast(repeat ? `Próxima abordagem em ${days} dias, repetindo a cada ${days}` : `Próxima abordagem em ${days} dias`);
  };

  // "Dispensar" no card: tira a data e encerra o ciclo de follow-up
  const handleEndFollowup = async (leadId) => {
    await leadsCol().doc(leadId).update({ next_contact_date: null, followup_days: null, updated_at: new Date().toISOString() });
  };

  const handleDismissNotification = async (lead) => {
    const proxima = proximaAbordagem(lead, null); // null sem cadência: limpa, como antes
    await leadsCol().doc(lead.id).update({
      next_contact_date: proxima,
      last_contacted_at: new Date().toISOString()
    });
    if (proxima) showToast(`Feito. Próxima abordagem em ${lead.followup_days} dias`);
  };

  const handleAddInteraction = async (leadId, form) => {
    const lead = leads.find(l=>l.id===leadId);
    const newInter = { id:Date.now().toString(), ...form, created_at:new Date().toISOString() };
    // arrayUnion acrescenta sem sobrescrever o array: não perde a interação de quem salvou junto
    const proxima = proximaAbordagem(lead, form.date || null); // com cadência, conta como contato feito
    const update = { interactions: FieldValue.arrayUnion(newInter), updated_at:new Date().toISOString() };
    if (proxima) update.next_contact_date = proxima;
    try {
      await leadsCol().doc(leadId).update(update);
      await writeAudit({ type:'interaction_add', lead_id:leadId, lead_name:lead?.company_name, description:`Adicionou interação em "${lead?.company_name}"`, before:{interactions:lead?.interactions||[]}, after:{interaction_id:newInter.id} });
      showToast(proxima ? `Interação registrada. Próxima abordagem em ${lead.followup_days} dias` : 'Interação registrada');
    } catch(e) { showToast('Erro ao salvar interação', 'error'); }
  };

  const handleAddTimelineNote = async (leadId, entry) => {
    const lead = leads.find(l=>l.id===leadId);
    const newEntry = { id:Date.now().toString(), ...entry, created_at:new Date().toISOString() };
    await leadsCol().doc(leadId).update({ timeline: FieldValue.arrayUnion(newEntry), updated_at:new Date().toISOString() });
    await writeAudit({ type:'timeline_add', lead_id:leadId, lead_name:lead?.company_name, description:`Adicionou nota na linha do tempo de "${lead?.company_name}"`, before:{timeline:lead?.timeline||[]}, after:{entry_id:newEntry.id} });
  };

  const handleEditTimelineNote = async (leadId, entryId, data) => {
    const lead = leads.find(l=>l.id===leadId);
    await patchArray(leadId, 'timeline', arr => arr.map(e => e.id===entryId ? { ...e, ...data } : e));
    await writeAudit({ type:'timeline_add', lead_id:leadId, lead_name:lead?.company_name, description:`Editou nota na linha do tempo de "${lead?.company_name}"`, before:{timeline:lead?.timeline||[]}, after:null });
  };

  const handleDeleteTimelineNote = async (leadId, entryId) => {
    const lead = leads.find(l=>l.id===leadId);
    await patchArray(leadId, 'timeline', arr => arr.filter(e => e.id!==entryId));
    await writeAudit({ type:'timeline_delete', lead_id:leadId, lead_name:lead?.company_name, description:`Excluiu nota da linha do tempo de "${lead?.company_name}"`, before:{timeline:lead?.timeline||[]}, after:null });
  };

  const handleEditInteraction = async (leadId, interactionId, data) => {
    const lead = leads.find(l=>l.id===leadId);
    await patchArray(leadId, 'interactions', arr => arr.map(i => i.id===interactionId ? { ...i, ...data } : i));
    await writeAudit({ type:'interaction_add', lead_id:leadId, lead_name:lead?.company_name, description:`Editou interação em "${lead?.company_name}"`, before:{interactions:lead?.interactions||[]}, after:null });
  };

  const handleDeleteInteraction = async (leadId, interactionId) => {
    const lead = leads.find(l=>l.id===leadId);
    await patchArray(leadId, 'interactions', arr => arr.filter(i => i.id!==interactionId));
    await writeAudit({ type:'interaction_delete', lead_id:leadId, lead_name:lead?.company_name, description:`Excluiu interação em "${lead?.company_name}"`, before:{interactions:lead?.interactions||[]}, after:{interaction_id:interactionId} });
  };

  const handleSaveDeal = async (leadId, dealData) => {
    const lead = leads.find(l=>l.id===leadId);
    try {
      await leadsCol().doc(leadId).update({ deal: dealData, updated_at:new Date().toISOString() });
      await writeAudit({ type:'deal_update', lead_id:leadId, lead_name:lead?.company_name, description:`Atualizou deal de "${lead?.company_name}"`, before:{deal:lead?.deal||null}, after:{deal:dealData} });
      showToast('Deal atualizado');
    } catch(e) { showToast('Erro ao salvar deal', 'error'); }
  };

  // ── Operações de Parceiro ──
  const handleCreatePartner = async (form) => {
    await partnersCol().add({ ...form, created_at:new Date().toISOString() });
  };
  const handleUpdatePartner = async (id, form) => {
    await partnersCol().doc(id).update(form);
  };
  const handleDeletePartner = async (id) => {
    await partnersCol().doc(id).delete();
  };
  const handleGeneratePortalLink = async (partnerId) => {
    const partner = partners.find(p => p.id === partnerId);
    // Revogar token anterior, se existir
    if (partner?.portal_token) {
      await db.collection('portals').doc(partner.portal_token).delete().catch(()=>{});
    }
    const token = genToken();
    await db.collection('portals').doc(token).set({
      partner_id: partnerId,
      company_id: company,
      created_at: new Date().toISOString()
    });
    await partnersCol().doc(partnerId).update({ portal_token: token });
    return token;
  };
  const handleRevokePortalLink = async (partnerId, token) => {
    await db.collection('portals').doc(token).delete().catch(()=>{});
    await partnersCol().doc(partnerId).update({ portal_token: null });
  };

  // ── Operações de Vendedor ──
  const handleCreateSeller = async (form) => {
    await sellersCol().add({ ...form, created_at:new Date().toISOString() });
  };
  const handleUpdateSeller = async (id, form) => {
    await sellersCol().doc(id).update(form);
  };
  const handleDeleteSeller = async (id) => {
    await sellersCol().doc(id).delete();
  };

  const openLeadFromKpis = (idOrLead) => {
    const lead = typeof idOrLead==='string' ? leads.find(l=>l.id===idOrLead) : idOrLead;
    if (lead) { setSelectedLead(lead); setTab('kanban'); }
  };

  const companyMeta = empresasPorId[company];
  // A paleta é do código; o banco só escolhe a cor. Enquanto o documento não
  // chega, o azul neutro segura a tela sem piscar.
  const accent = paleta(companyMeta?.color);

  if (migrating) return <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center gap-3">
    <Spinner />
    <p className="text-sm text-gray-500">Migrando dados do Produxia para a nova estrutura...</p>
  </div>;

  if (loading) return <div className="min-h-screen bg-gray-100 flex items-center justify-center"><Spinner /></div>;

  const pendingReferralsCount = referrals.filter(r => r.status === 'pending').length;

  // Mesma conta da tela de Perdidos, que já mostra perdido + repescagem.
  const lostCount = leads.filter(l=>CONTA_COMO_PERDA.includes(l.stage)).length;
  // Dois níveis apenas, conforme a HIG. Badge só onde pede ação.
  const navGroups = [
    { title:'Leads', items:[
      { key:'kanban',     label:'Pipeline',    icon:'pipeline'   },
      { key:'repasses',   label:'Fechados',    icon:'fechados'   },
      { key:'perdidos',   label:'Perdidos',    icon:'perdidos',   count:lostCount, muted:true },
      { key:'calendario', label:'Calendário',  icon:'calendario' },
      { key:'contatos',   label:'Contatos',    icon:'contatos'   },
    ]},
    { title:'Análise', items:[
      { key:'mes',        label:'Mês a mês',   icon:'mes'        },
      { key:'concluidas', label:'Concluídas',  icon:'concluidas' },
      { key:'dashboard',  label:'Dashboard',   icon:'dashboard'  },
      { key:'kpis',       label:'KPIs',        icon:'kpis'       },
    ]},
    { title:'Cadastros', items:[
      { key:'sellers',    label:'Vendedores',  icon:'vendedores' },
      { key:'partners',   label:'Parceiros',   icon:'parceiros'  },
      { key:'campos',     label:'Campos',      icon:'campos'     },
      { key:'sites',      label:'Sites',       icon:'sites'      },
      { key:'equipe',     label:'Equipe',      icon:'equipe'     },
    ]},
    { title:'Meu dia', items:[
      { key:'todos',      label:'Tarefas',     icon:'tarefas'    },
      { key:'indicacoes', label:'Indicações',  icon:'indicacoes', count:pendingReferralsCount, alert:true },
    ]},
  ];
  const tabTitle = navGroups.flatMap(g=>g.items).find(i=>i.key===tab)?.label || 'Pipeline';

  const currentLead = selectedLead ? (leads.find(l=>l.id===selectedLead.id) || selectedLead) : null;

  return <div className="min-h-screen bg-gray-100 flex">

    {/* ── Sidebar: navegação ── */}
    {sidebarOpen && <aside className={`w-56 flex-shrink-0 bg-white border-r border-black/[0.07] flex flex-col sticky top-0 h-screen ${accent.headerBorder}`}>
      {/* Escopo: empresa */}
      <div className="p-3 border-b border-black/[0.06]">
        <div className="relative">
          <select
            value={company}
            onChange={e=>switchCompany(e.target.value)}
            aria-label="Trocar de empresa"
            className={`appearance-none w-full pl-3 pr-8 py-2 rounded-xl text-sm font-semibold border cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 ${companyMeta ? `focus:ring-${companyMeta.color}-400` : 'focus:ring-blue-400'} ${companyMeta?.active || 'bg-white border-gray-300 text-gray-700'}`}
          >
            {allowedCompanies.map(c=>(
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
            <svg className="w-3.5 h-3.5 opacity-70" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd"/>
            </svg>
          </div>
        </div>
        {!isAdmin && <span className="mt-2 inline-block t-caption-2 bg-gray-100 text-gray-500 border border-gray-200 rounded-full px-2 py-0.5">Visualizador</span>}
      </div>

      {/* Destinos, agrupados */}
      <nav aria-label="Navegação principal" className="flex-1 overflow-y-auto px-2 py-3 space-y-4">
        {navGroups.map(g=>(
          <div key={g.title}>
            <p className="t-caption-2 text-gray-400 font-medium px-2.5 mb-1">{g.title}</p>
            <div className="space-y-0.5">
              {g.items.map(it=>{
                const active = tab===it.key;
                return <button key={it.key} onClick={()=>setTab(it.key)}
                  aria-current={active ? 'page' : undefined}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-sm transition-colors text-left ${active ? `${accent.navActive} font-semibold` : 'text-gray-600 hover:bg-black/[0.045]'}`}>
                  <NavIcon name={it.icon} />
                  <span className="flex-1 truncate">{it.label}</span>
                  {it.count > 0 && <span className={`t-caption-2 font-semibold px-1.5 py-0.5 rounded-full ${it.alert ? 'bg-red-100 text-red-600' : 'text-gray-400'}`}>
                    {it.count}
                  </span>}
                </button>;
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Conta */}
      <div className="p-3 border-t border-black/[0.06] flex items-center gap-2">
        <img src={user.photoURL||'https://ui-avatars.com/api/?name='+encodeURIComponent(user.displayName||user.email)} className="w-8 h-8 rounded-full flex-shrink-0" alt="" />
        <div className="flex-1 min-w-0">
          <p className="t-caption-2 font-medium text-gray-700 truncate">{user.displayName || user.email}</p>
        </div>
        <button onClick={()=>auth.signOut()} aria-label="Sair da conta"
          className="tap-safe t-caption-2 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0">Sair</button>
      </div>
    </aside>}

    {/* ── Coluna de conteúdo ── */}
    <div className="flex-1 min-w-0 flex flex-col">

      {/* Toolbar: título e ações da view */}
      <header className="glass border-b border-black/[0.07] sticky top-0 z-40">
        <div className="px-4 h-14 flex items-center gap-2">
          <button onClick={()=>setSidebarOpen(v=>!v)}
            aria-label={sidebarOpen ? 'Esconder menu lateral' : 'Mostrar menu lateral'}
            aria-expanded={sidebarOpen}
            className="tap-safe p-2 rounded-xl hover:bg-black/[0.06] text-gray-500 transition-colors flex-shrink-0">
            <NavIcon name="sidebar" className="w-5 h-5" />
          </button>
          <h1 className="t-headline font-semibold text-gray-900 truncate">{tabTitle}</h1>

          <div className="flex items-center gap-1.5 ml-auto flex-shrink-0">
            <button onClick={()=>setShowGlobalSearch(true)} aria-label="Busca global em todas as empresas" title="Busca global"
              className="tap-safe p-2 rounded-xl hover:bg-black/[0.06] text-gray-500 transition-colors">
              <NavIcon name="busca" className="w-5 h-5" />
            </button>
            <TemaToggle />
            <NotificationBell leads={leads} onOpenLead={lead=>{setSelectedLead(lead);setTab('kanban');}} onDismiss={handleDismissNotification} onStageChange={canEdit ? handleStageChange : null} />
            {isAdmin && <button onClick={()=>setShowAudit(s=>!s)}
              aria-label="Histórico de atividades" aria-pressed={showAudit} title="Histórico de atividades"
              className={`tap-safe p-2 rounded-xl transition-colors ${showAudit ? accent.audit : 'hover:bg-black/[0.06] text-gray-500'}`}>
              <NavIcon name="historico" className="w-5 h-5" />
            </button>}
            {/* Ação primária, na ponta trailing */}
            {canEdit && tab==='kanban' && (
              <button onClick={()=>setShowNewLead(true)}
                className={`${accent.btn} text-sm px-4 py-2 rounded-xl whitespace-nowrap font-semibold shadow-sm transition-colors ml-1`}>
                Novo lead
              </button>
            )}
          </div>
        </div>
      </header>

    {/* Conteúdo */}
    <main className="flex-1 w-full px-4 py-5">
      {tab==='kanban'    && <KanbanView    leads={leads} partners={partners} sellers={sellers} company={company} camposCustom={camposCustom} onStageChange={handleStageChange} onOpenLead={setSelectedLead} onTemperatureChange={handleToggleLeadFlag} onAddInteraction={handleAddInteraction} onClearDate={handleEndFollowup} onSnoozeDate={handleSnoozeDate} onImportLeads={canEdit ? handleImportLeads : null} isAdmin={canEdit} userEmail={user.email} accentConvBar={accent.convBar} />}

      {tab==='perdidos'  && <LostLeadsView  leads={leads} />}
      {tab==='sellers'   && <SellersView   sellers={sellers} onCreateSeller={handleCreateSeller} onUpdateSeller={handleUpdateSeller} onDeleteSeller={handleDeleteSeller} isAdmin={canEdit} />}
      {tab==='campos'    && <CamposView campos={camposCustom} company={company} companyLabel={companyMeta?.label} isAdmin={canEdit} onSalvar={handleSalvarCampos} />}
      {tab==='sites'     && <SitesView company={company} companyLabel={companyMeta?.label} isAdmin={isAdmin} />}
      {tab==='equipe'    && <EquipeView company={company} companyLabel={companyMeta?.label} isAdmin={isAdmin} meuEmail={user.email} />}
      {tab==='partners'  && <PartnersView  partners={partners} onCreatePartner={handleCreatePartner} onUpdatePartner={handleUpdatePartner} onDeletePartner={handleDeletePartner} onGeneratePortalLink={handleGeneratePortalLink} onRevokePortalLink={handleRevokePortalLink} isAdmin={canEdit} />}
      {tab==='dashboard' && <DashboardView leads={leads} partners={partners} onTogglePaid={canEdit ? handleTogglePartnerPaid : null} />}
      {tab==='mes'       && <MesView       leads={leads} onOpenLead={setSelectedLead} />}
      {tab==='kpis'      && <KPIsView      leads={leads} sellers={sellers} onOpenLead={openLeadFromKpis} company={company} />}
      {tab==='repasses'  && <RepAssesView  leads={leads} onToggle={handleToggleLeadFlag} onTogglePaid={canEdit ? handleTogglePartnerPaid : null} />}
      {tab==='calendario' && <CalendarView  leads={leads} onOpenLead={openLeadFromKpis} />}
      {tab==='contatos'   && <ContatosView  contatos={contatos} leads={leads} onOpenLead={openLeadFromKpis} />}
      {tab==='concluidas' && <ConcluidasView leads={leads} sellers={sellers} onOpenLead={openLeadFromKpis} />}
      {tab==='todos'      && <TodosView      empresas={allowedCompanies} userEmail={user.email} leads={leads} onToggleTask={handleToggleTask} onOpenLead={id=>{ const l = leads.find(x=>x.id===id); if (l) setSelectedLead(l); }} />}
      {tab==='indicacoes' && <PendingReferralsView referrals={referrals} company={company} partners={partners} isAdmin={isAdmin} onApprove={async (ref) => {
        // Aprovar: cria lead e marca referral como aprovada
        const now = new Date().toISOString();
        const leadData = {
          company_name: ref.company_name,
          contact_name: ref.contact_name || '',
          contact_phone: ref.contact_phone || '',
          contact_email: ref.contact_email || '',
          website: ref.website || '',
          stage: 'backlog',
          value: 0,
          notes: `Indicado por: ${ref.consultant_name}\n\n${ref.summary || ''}`.trim(),
          next_contact_date: null,
          partners: [],
          source: 'referral',
          referral_id: ref.id,
          referred_by: ref.consultant_name,
          qualification: ref.qualification,
          interactions: [],
          created_at: now,
          updated_at: now,
        };
        const novoLead = await db.collection('companies').doc(company).collection('leads').add(leadData);
        try { await vincularContato(novoLead.id, leadData); } catch (e) { console.warn('Indicação aprovada sem contato vinculado', e); }
        await db.collection('companies').doc(company).collection('referrals').doc(ref.id).update({ status: 'approved', reviewed_at: now });
      }} onReject={async (id) => {
        await db.collection('companies').doc(company).collection('referrals').doc(id).update({ status: 'rejected', reviewed_at: new Date().toISOString() });
      }} />}
    </main>
    </div>

    {/* Modais */}
    {showNewLead && <Modal title={`Novo Lead — ${companyMeta?.label}`} onClose={()=>setShowNewLead(false)} wide>
      <LeadForm partners={partners} sellers={sellers} company={company} leads={leads} camposCustom={camposCustom} vendedorPadrao={vendedorPadrao} onSave={handleCreateLead} onCancel={()=>setShowNewLead(false)} />
    </Modal>}
    {editingLead && <Modal title={`Editar: ${editingLead.company_name}`} onClose={()=>setEditingLead(null)} wide>
      <LeadForm lead={editingLead} partners={partners} sellers={sellers} company={company} leads={leads} camposCustom={camposCustom} onSave={d=>handleUpdateLead(editingLead.id,d)} onCancel={()=>setEditingLead(null)} />
    </Modal>}
    {currentLead && <LeadDetailModal
      lead={currentLead}
      onClose={()=>setSelectedLead(null)}
      onEdit={lead=>{setEditingLead(lead);setSelectedLead(null);}}
      onDelete={handleDeleteLead}
      onAddInteraction={handleAddInteraction}
      onEditInteraction={handleEditInteraction}
      onDeleteInteraction={handleDeleteInteraction}
      onAddTimelineNote={handleAddTimelineNote}
      onEditTimelineNote={handleEditTimelineNote}
      onDeleteTimelineNote={handleDeleteTimelineNote}
      onSaveDeal={handleSaveDeal}
      onMove={handleMoveLead}
      moveTargets={allowedCompanies.filter(c=>c.id!==company)}
      isAdmin={canEdit}
      contatos={contatos}
      leads={leads}
      onOpenLead={setSelectedLead}
      onStageChange={handleStageChange}
      company={company}
      camposCustom={camposCustom}
      onAddTask={handleAddTask}
      onToggleTask={handleToggleTask}
      onDeleteTask={handleDeleteTask}
    />}
    {showAudit && isAdmin && <AuditSidebar company={company} isAdmin={isAdmin} onRollback={handleRollback} onClose={()=>setShowAudit(false)} />}
    {showConfetti && <Confetti onDone={() => setShowConfetti(false)} />}
    {showGlobalSearch && <GlobalSearchModal
      allowedCompanies={allowedCompanies}
      onClose={()=>setShowGlobalSearch(false)}
      onSelectLead={(lead, companyId) => {
        setShowGlobalSearch(false);
        if (companyId !== company) switchCompany(companyId);
        setTimeout(() => setSelectedLead(lead), companyId !== company ? 600 : 0);
        setTab('kanban');
      }}
    />}
    {toast && <Toast key={toast.key} message={toast.message} type={toast.type} onDone={()=>setToast(null)} />}
  </div>;
}

// ─── PendingReferralsView ─────────────────────────────────

export { MainApp };
